import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import {
  RiAddLine, RiEditLine, RiDeleteBin6Line, RiCloseLine,
  RiCoupon3Line, RiCheckLine,
} from 'react-icons/ri'
import { couponsAPI } from '../../api'

const empty = {
  code: '', description: '', type: 'percentage', value: '',
  min_order_amount: '', max_uses: '', expires_at: '', is_active: true,
}

export default function ManageCoupons() {
  const qc = useQueryClient()
  const [open, setOpen]   = useState(false)
  const [form, setForm]   = useState(empty)
  const [editingId, setEditingId] = useState(null)

  const { data = [], isLoading } = useQuery({
    queryKey: ['admin-coupons'],
    queryFn:  () => couponsAPI.getAll().then(r => r.data?.data ?? r.data ?? []),
  })

  const upsertMut = useMutation({
    mutationFn: (payload) => editingId
      ? couponsAPI.update(editingId, payload)
      : couponsAPI.create(payload),
    onSuccess: () => {
      toast.success(editingId ? 'Coupon updated.' : 'Coupon created.')
      qc.invalidateQueries({ queryKey: ['admin-coupons'] })
      closeModal()
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Save failed.'),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => couponsAPI.delete(id),
    onSuccess: () => { toast.success('Coupon deleted.'); qc.invalidateQueries({ queryKey: ['admin-coupons'] }) },
    onError:   (e) => toast.error(e?.response?.data?.message || 'Delete failed.'),
  })

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true) }
  const openEdit   = (c) => {
    setForm({
      code: c.code,
      description: c.description ?? '',
      type: c.type,
      value: c.value,
      min_order_amount: c.min_order_amount ?? '',
      max_uses: c.max_uses ?? '',
      expires_at: c.expires_at ? c.expires_at.split('T')[0] : '',
      is_active: !!c.is_active,
    })
    setEditingId(c.id)
    setOpen(true)
  }
  const closeModal = () => { setOpen(false); setEditingId(null); setForm(empty) }

  const onSubmit = (e) => {
    e.preventDefault()
    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      value: parseFloat(form.value),
      min_order_amount: form.min_order_amount === '' ? null : parseFloat(form.min_order_amount),
      max_uses: form.max_uses === '' ? null : parseInt(form.max_uses, 10),
      expires_at: form.expires_at || null,
    }
    upsertMut.mutate(payload)
  }

  const onDelete = (c) => {
    if (!confirm(`Delete coupon "${c.code}"? This cannot be undone.`)) return
    deleteMut.mutate(c.id)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl text-salon-dark font-bold">Coupons</h1>
          <p className="text-salon-muted font-body text-sm">Create, edit and retire promotional codes.</p>
        </div>
        <button onClick={openCreate} className="btn-primary">
          <RiAddLine /> New Coupon
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-salon-muted font-body">Loading coupons…</div>
        ) : data.length === 0 ? (
          <div className="p-16 text-center">
            <RiCoupon3Line className="text-6xl text-salon-cream-dark mx-auto mb-3" />
            <p className="text-salon-muted font-body">No coupons yet.</p>
            <button onClick={openCreate} className="btn-primary mt-4">Create your first coupon</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead className="bg-salon-cream/60 text-left text-xs uppercase tracking-wide text-salon-muted">
                <tr>
                  <th className="px-5 py-3">Code</th>
                  <th className="px-5 py-3">Description</th>
                  <th className="px-5 py-3">Discount</th>
                  <th className="px-5 py-3">Min Order</th>
                  <th className="px-5 py-3">Used</th>
                  <th className="px-5 py-3">Expires</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-salon-cream-dark">
                {data.map(c => (
                  <tr key={c.id} className="hover:bg-salon-cream/30 transition-colors">
                    <td className="px-5 py-4 font-mono font-semibold text-salon-primary">{c.code}</td>
                    <td className="px-5 py-4 text-salon-dark max-w-xs truncate">{c.description || '—'}</td>
                    <td className="px-5 py-4 text-salon-dark">
                      {c.type === 'percentage'
                        ? <span className="font-semibold">{parseFloat(c.value)}% off</span>
                        : <span className="font-semibold">₹{parseFloat(c.value).toLocaleString()} off</span>}
                    </td>
                    <td className="px-5 py-4 text-salon-muted">
                      {c.min_order_amount ? `₹${parseFloat(c.min_order_amount).toLocaleString()}` : '—'}
                    </td>
                    <td className="px-5 py-4 text-salon-muted">
                      {c.used_count ?? 0}{c.max_uses ? ` / ${c.max_uses}` : ''}
                    </td>
                    <td className="px-5 py-4 text-salon-muted">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : 'Never'}
                    </td>
                    <td className="px-5 py-4">
                      {c.is_active
                        ? <span className="badge badge-green">Active</span>
                        : <span className="badge badge-red">Disabled</span>}
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(c)} className="p-2 rounded-lg text-salon-primary hover:bg-salon-primary/10" aria-label="Edit">
                        <RiEditLine />
                      </button>
                      <button onClick={() => onDelete(c)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" aria-label="Delete">
                        <RiDeleteBin6Line />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-salon-dark/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-lg bg-white rounded-3xl shadow-salon-lg overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-salon-primary text-white px-6 py-5 flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-xs uppercase tracking-widest font-body">{editingId ? 'Edit' : 'New'}</p>
                  <h2 className="font-display text-xl font-bold">Coupon</h2>
                </div>
                <button onClick={closeModal} className="p-2 rounded-lg hover:bg-white/10"><RiCloseLine className="text-xl" /></button>
              </div>

              <form onSubmit={onSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label">Code *</label>
                    <input
                      required maxLength={32}
                      value={form.code}
                      onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                      placeholder="WELCOME20"
                      className="input-field font-mono uppercase"
                    />
                  </div>
                  <div>
                    <label className="input-label">Type *</label>
                    <select
                      value={form.type}
                      onChange={e => setForm({ ...form, type: e.target.value })}
                      className="input-field"
                    >
                      <option value="percentage">% Percentage</option>
                      <option value="fixed">₹ Fixed</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="input-label">Description</label>
                  <input
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="20% off your first visit"
                    className="input-field"
                    maxLength={120}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label">Value * {form.type === 'percentage' ? '(%)' : '(₹)'}</label>
                    <input
                      required type="number" step="0.01" min="0"
                      max={form.type === 'percentage' ? 100 : undefined}
                      value={form.value}
                      onChange={e => setForm({ ...form, value: e.target.value })}
                      placeholder={form.type === 'percentage' ? '20' : '500'}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="input-label">Min order (₹)</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form.min_order_amount}
                      onChange={e => setForm({ ...form, min_order_amount: e.target.value })}
                      placeholder="1500"
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="input-label">Max uses</label>
                    <input
                      type="number" min="1"
                      value={form.max_uses}
                      onChange={e => setForm({ ...form, max_uses: e.target.value })}
                      placeholder="Unlimited"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="input-label">Expires on</label>
                    <input
                      type="date"
                      value={form.expires_at}
                      onChange={e => setForm({ ...form, expires_at: e.target.value })}
                      className="input-field"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-3 select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })}
                    className="w-5 h-5 accent-salon-primary"
                  />
                  <span className="font-body text-salon-dark text-sm">Active — coupon can be redeemed</span>
                </label>

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={closeModal} className="btn-ghost">Cancel</button>
                  <button type="submit" disabled={upsertMut.isPending} className="btn-primary">
                    {upsertMut.isPending ? 'Saving…' : <><RiCheckLine /> {editingId ? 'Save changes' : 'Create coupon'}</>}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
